import type { Cell, Grid } from '../api/types'

export type StatusKind = 'untranslated' | 'ai' | 'manual' | 'needs-review' | 'stale'

export const STATUS_ORDER: StatusKind[] = [
  'untranslated',
  'ai',
  'manual',
  'needs-review',
  'stale',
]

/** Grid cells only carry staleness once the API compares source hashes. Until
 *  then 'stale' is not an offerable filter — a control that can never match is
 *  worse than no control. Detected from the payload, so it lights up by itself
 *  the moment the backend adds the field. */
export function staleSupported(grid: Grid | undefined): boolean {
  for (const key of grid?.keys ?? []) {
    for (const cells of Object.values(key.cells)) {
      for (const cell of cells) return 'stale' in cell
    }
  }
  return false
}

export const filterableStatuses = (grid: Grid | undefined): StatusKind[] =>
  staleSupported(grid) ? STATUS_ORDER : STATUS_ORDER.filter((kind) => kind !== 'stale')

export interface StatusSpec {
  kind: StatusKind
  /** Glyph carries the meaning when colour is unavailable. */
  glyph: string
  short: string
  label: string
  /** Full sentence for screen readers and tooltips. */
  description: string
  className: string
}

export const STATUS: Record<StatusKind, StatusSpec> = {
  untranslated: {
    kind: 'untranslated',
    glyph: '○',
    short: 'None',
    label: 'Untranslated',
    description: 'No translation exists for this cell yet.',
    className: 'border-dotted border-ink-500 bg-white text-ink-600',
  },
  ai: {
    kind: 'ai',
    glyph: '◆',
    short: 'AI',
    label: 'AI translated',
    description: 'Machine translated. AI may overwrite it again until a human edits it.',
    className: 'border-violet-300 bg-violet-50 text-violet-800',
  },
  manual: {
    kind: 'manual',
    glyph: '✓',
    short: 'Manual',
    label: 'Human translated',
    description: 'Edited by a human and locked against AI overwrites.',
    className: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  },
  'needs-review': {
    kind: 'needs-review',
    glyph: '!',
    short: 'Review',
    label: 'Needs review',
    description: 'Flagged for review — the English source changed or lint found a mismatch.',
    className: 'border-amber-400 bg-amber-50 text-amber-900',
  },
  stale: {
    kind: 'stale',
    glyph: '≠',
    short: 'Stale',
    label: 'Stale source',
    description: 'The English source changed after this translation was written.',
    className: 'border-dashed border-orange-400 bg-orange-50 text-orange-900',
  },
}

export function cellStatus(cell: Cell): StatusKind {
  if (cell.origin === 'untranslated') return 'untranslated'
  if (cell.stale) return 'stale'
  if (cell.needs_review) return 'needs-review'
  return cell.status === 'manual' ? 'manual' : 'ai'
}

/** A key matches a status filter when any of its cells in the shown locales does. */
export function keyMatchesStatus(
  cells: Record<string, Cell[]>,
  locales: string[],
  wanted: StatusKind,
): boolean {
  return locales.some((code) =>
    (cells[code] ?? []).some((cell) => cellStatus(cell) === wanted),
  )
}
